import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap';

import axios, { apiBaseUrl } from '../api/httpClient';
import './OperationalAutomationsPage.css';

type RunStatus = 'running' | 'successful' | 'failed';
type FindingStatus = 'open' | 'resolved';

interface AutomationRun {
  id: string;
  group: string;
  status: RunStatus;
  startedAt: string;
  completedAt: string | null;
  sourceCounts: Record<string, number>;
  resultCounts: Record<string, {
    open: number;
    new: number;
    changed: number;
    resolved: number;
    metrics: Record<string, unknown>;
  }>;
  safeError: string | null;
}

interface AutomationCheck {
  key: string;
  group: string;
  title: string;
  openCount: number;
  criticalCount: number;
  latestRun: AutomationRun | null;
}

interface AutomationFinding {
  id: number;
  checkKey: string;
  findingKey: string;
  severity: 'critical' | 'warning';
  title: string;
  summary: string;
  evidence: Record<string, unknown>;
  status: FindingStatus;
  firstSeenAt: string;
  lastSeenAt: string;
  resolvedAt: string | null;
}

interface AutomationResponse {
  enabled: boolean;
  customerEstimateDeliveryEnabled: boolean;
  latestRuns: Record<string, AutomationRun | null>;
  checks: AutomationCheck[];
  findings: AutomationFinding[];
}

const groups = [
  { key: 'finance', label: 'Finance', source: 'FreeAgent' },
  { key: 'integrity', label: 'Project integrity', source: 'FreeAgent + Jira' },
  { key: 'timesheets', label: 'Timesheets', source: 'FreeAgent' },
];

const formatTimestamp = (value: string | null): string => {
  if (!value) return 'Never';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('en-GB');
};

const statusVariant = (status?: RunStatus): string => {
  if (status === 'successful') return 'success';
  if (status === 'failed') return 'danger';
  return 'secondary';
};

const humaniseKey = (key: string): string => key.replace(/_/g, ' ');
const pageSize = 50;

const OperationalAutomationsPage: React.FC = () => {
  const [data, setData] = useState<AutomationResponse | null>(null);
  const [status, setStatus] = useState<'open' | 'resolved' | 'all'>('open');
  const [check, setCheck] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get<AutomationResponse>(`${apiBaseUrl}/operational-automations`, {
        params: { status, ...(check ? { check } : {}) },
      });
      setData(response.data);
      setPage(1);
    } catch {
      setError('The direct-source automation status could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [check, status]);

  useEffect(() => {
    load();
  }, [load]);

  const checkTitles = useMemo(
    () => new Map((data?.checks ?? []).map((item) => [item.key, item.title])),
    [data]
  );
  const findings = data?.findings ?? [];
  const pageCount = Math.max(1, Math.ceil(findings.length / pageSize));
  const visibleFindings = findings.slice((page - 1) * pageSize, page * pageSize);

  return (
    <Container className="operational-automations py-4">
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
        <div>
          <p className="operational-automations__eyebrow">GCM operations</p>
          <h2>Operational automations</h2>
          <p className="text-muted mb-0">Live checks against FreeAgent and Jira, independent of reporting imports.</p>
        </div>
        <Button variant="outline-primary" onClick={load} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh status'}
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {data && !data.enabled && (
        <Alert variant="warning">
          Scheduling is disabled in this environment. Verification runs can still populate this page without sending mail.
        </Alert>
      )}
      {data && !data.customerEstimateDeliveryEnabled && (
        <Alert variant="info">
          Estimate reminders are internal findings only; no messages are sent to customers.
        </Alert>
      )}

      {loading && !data ? (
        <div className="operational-automations__loading"><Spinner animation="border" /><span>Loading direct-source status…</span></div>
      ) : (
        <>
          <Row className="g-3 mb-4">
            {groups.map((group) => {
              const run = data?.latestRuns[group.key] ?? null;
              const sourceSummary = run
                ? Object.entries(run.sourceCounts).map(([key, value]) => `${value} ${humaniseKey(key)}`).join(' · ')
                : 'No source snapshot yet';
              return (
                <Col md={4} key={group.key}>
                  <Card className="h-100 operational-automations__run-card">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div>
                          <Card.Title>{group.label}</Card.Title>
                          <Card.Subtitle className="text-muted">Direct: {group.source}</Card.Subtitle>
                        </div>
                        <Badge bg={statusVariant(run?.status)}>{run?.status ?? 'not run'}</Badge>
                      </div>
                      <p className="mt-3 mb-1"><strong>Last run:</strong> {formatTimestamp(run?.completedAt ?? run?.startedAt ?? null)}</p>
                      <p className="small text-muted mb-0">{sourceSummary}</p>
                      {run?.safeError && <p className="text-danger small mt-2 mb-0">Safe error: {run.safeError}</p>}
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>

          <Row className="g-3 mb-4">
            {(data?.checks ?? []).map((item) => (
              <Col sm={6} lg={3} key={item.key}>
                <button
                  type="button"
                  className={`operational-automations__check ${check === item.key ? 'is-selected' : ''}`}
                  onClick={() => setCheck(check === item.key ? '' : item.key)}
                  aria-pressed={check === item.key}
                >
                  <span>{item.title}</span>
                  <strong>{item.openCount}</strong>
                  <small>{item.criticalCount > 0 ? `${item.criticalCount} critical` : 'open findings'}</small>
                </button>
              </Col>
            ))}
          </Row>

          <Card>
            <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-3">
              <strong>Findings</strong>
              <div className="d-flex flex-wrap gap-2">
                <Form.Select aria-label="Finding status" value={status} onChange={(event) => setStatus(event.target.value as 'open' | 'resolved' | 'all')}>
                  <option value="open">Open</option>
                  <option value="resolved">Resolved</option>
                  <option value="all">All</option>
                </Form.Select>
                {check && <Button variant="outline-secondary" size="sm" onClick={() => setCheck('')}>Clear check filter</Button>}
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {findings.length === 0 ? (
                <p className="text-muted p-4 mb-0">No findings match the current filters.</p>
              ) : (
                <Table responsive hover className="mb-0 align-middle">
                  <thead>
                    <tr><th>Check</th><th>Severity</th><th>Finding</th><th>Last seen</th><th>Evidence</th></tr>
                  </thead>
                  <tbody>
                    {visibleFindings.map((finding) => (
                      <tr key={finding.id}>
                        <td>{checkTitles.get(finding.checkKey) ?? humaniseKey(finding.checkKey)}</td>
                        <td><Badge bg={finding.severity === 'critical' ? 'danger' : 'warning'} text={finding.severity === 'warning' ? 'dark' : undefined}>{finding.severity}</Badge></td>
                        <td><strong>{finding.title}</strong><div className="small text-muted">{finding.summary}</div></td>
                        <td>{formatTimestamp(finding.lastSeenAt)}</td>
                        <td>
                          <details>
                            <summary>View</summary>
                            <pre>{JSON.stringify(finding.evidence, null, 2)}</pre>
                          </details>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
            {findings.length > pageSize && (
              <Card.Footer className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                <span className="small text-muted">
                  Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, findings.length)} of {findings.length}
                </span>
                <div className="d-flex gap-2">
                  <Button variant="outline-secondary" size="sm" disabled={page <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>Previous</Button>
                  <span className="align-self-center small">Page {page} of {pageCount}</span>
                  <Button variant="outline-secondary" size="sm" disabled={page >= pageCount} onClick={() => setPage((value) => Math.min(pageCount, value + 1))}>Next</Button>
                </div>
              </Card.Footer>
            )}
          </Card>
        </>
      )}
    </Container>
  );
};

export default OperationalAutomationsPage;
